import React, { useMemo } from 'react';
import { useSlate } from 'slate-react';
import { Node, Transforms, Editor } from 'slate';
import { ReactEditor } from 'slate-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { getBlockAbove, getNodes, getAbove } from '@udecode/slate-plugins';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ListGroupItem, Media, Button } from 'reactstrap';

import { getOrdinalNumber } from './Elements'
import { useEditorState, useEditorActions } from '../../editor-context'
import { scrollToNode, isAlignBlock } from '../../Canvas/TratoFormat';
import { useHover } from 'hooks/useHover';

import { extractHTMLContent } from 'utils'



  
  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
  
    // change background colour if dragging
    background: isDragging ? '#45616e' : '#354f5c',
  
    // styles we need to apply on draggables
    ...draggableStyle
  });
  
function reindex(editor, type) {
    const allNodes = [...Editor.nodes(editor, {
        at: {
            anchor: { path: [0], offset: 0 },
            focus: { path: [editor.children.length - 1], offset: 0 }
        },
        mode: 'all',
        match: (n) => n.type === type
    })]

    let block = 1;
    let number = 1;
    allNodes.map(([element, elementLocation]) => {
        if (element.block !== block){
            block = element.block;
            number = 1;
        }

        Transforms.setNodes(editor, { number }, {
            at: elementLocation
        })

        number++;
    })
}

export function TratoFormatIndex() {
    const { t } = useTranslation();
    const editor = useSlate();
    const { isEditable, currentElement, document: doc } = useEditorState();
    const { removeElement, setCurrentElement } = useEditorActions()

    const handleRemove = (element) => {
        removeElement({ elementId: element._id })
        let el = element;
        if (isAlignBlock(element.type)) {
            el = element.children[0];
        }

        if (['statement', 'clause'].includes(el.type)) {
            reindex(editor, el.type)
        }
    }

    const handleDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        
        //Getting nodeToMove
        let [nodeToMove, nodeToMoveLocation] = [...getNodes(editor, {
            at: [result.source.index]
        })][1] ?? []
        
        // if node is a an align block use children;
        if (isAlignBlock(nodeToMove.type)) {
            nodeToMove = nodeToMove.children[0];
            nodeToMoveLocation = ReactEditor.findPath(editor, nodeToMove)
        }

        if (['statement', 'clause'].includes(nodeToMove.type)) {

            // Get all nodes of same type before destination
            const nodesBeforeDestination = [...getNodes(editor, {
                at: {
                    anchor: { path: [0], offset: 0 },
                    focus: { path: [Math.max(result.destination.index, 0)], offset: 0 }
                },
                mode: 'highest',
                match: (n) => n.type === nodeToMove.type && n._id !== nodeToMove._id
            })]

            // get nearest node of same type form destination
            const [nearestNode] = nodesBeforeDestination.pop() ?? [];
            const newBlock = nearestNode?.block || 1;
            const newNumber = (nearestNode?.number || nearestNode?.index) || 0;

            // set target node new properties
            Transforms.setNodes(editor, { nodesBeforeDestination, block: newBlock, number: newNumber + 1 }, {
                at: nodeToMoveLocation
            })

            // move node
            Transforms.moveNodes(editor, {
                at: [result.source.index],
                to: [result.destination.index]
            })
            // reindex all nodes of same type as target
            reindex(editor, nodeToMove.type)
        }  else {
             // move node
             Transforms.moveNodes(editor, {
                at: [result.source.index],
                to: [result.destination.index]
            })
        }
    }


    const handleClick = (element) => {
        const path = ReactEditor.findPath(editor, element)
        Transforms.select(editor, path)
        setCurrentElement({ element, location: path })
        scrollToNode(editor, element)
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" isDropDisabled={!isEditable}  >
                {(provided, snapshot) => (
                    <div 
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="sidebar-index-content"
                    >
                        {editor.children.map((element, index) => (
                            <Draggable 
                                key={element._id} 
                                draggableId={element._id} 
                                index={index}
                                isDragDisabled={!isEditable} 
                            >
                                {(provided, snapshot) => (
                                    <IndexElement 
                                        key={element._id}
                                        element={element} 
                                        currentElementId={currentElement._id} 
                                        isEditable={isEditable} 
                                        onRemove={handleRemove}
                                        onClick={handleClick}
                                        isDocumentMigrated={doc.isMigrated}
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style
                                        )}
                                    />
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        {
                            !editor.children.length &&
                            <div className="empty-element text-center text-dark p-2">{ t('editor.index.empty')}</div>         
                        }
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    )
}

const IndexElement =  React.forwardRef(({ element, currentElementId, isEditable, isDocumentMigrated, onRemove, onClick, ...props }, ref) => {
    const { t } = useTranslation();
    const canFixClauseOrder = true; //FIX_CLAUSE_ORDER
  
    const realElement = useMemo(() => {
        return isAlignBlock(element.type) ? element.children[0] : element;
    }, [element])

    const content = useMemo(() => {
        if (['signature', 'break'].includes(realElement.type)) {
            return null;
        }
        if (!isDocumentMigrated) return extractHTMLContent(element.content);
        // cutText content
        const text = Node.string(realElement);

        const defaultText = ['signature', 'breakline'].includes(realElement.type) ?  
                                t(`editor.index.${realElement.type}`)
                            : ['table-wrap', 'table'].includes(realElement.type) ?  
                                t('editor.elements.table_title')
                                : t(`editor.items.${realElement.type}`)

        return text.length ? text : defaultText
    }, [realElement, t])


    const handleRemove = () => {
        onRemove(element);
    }
    const handleClick = () => {
        onClick(realElement);
    }

    return (
        <div ref={ref} className={clsx('text-dark p-0', realElement._id === currentElementId && 'active')}{...props}>
        <Media color="light" className={clsx('d-flex align-items-center bg-light border-bottom border-top',  realElement._id === currentElementId ? 'border border-primary' : '')}>
            <Media className={clsx('mr-2 p-3  border-right',  realElement._id === currentElementId ? 'bg-orange-light' : 'bg-light-contrast')}  onClick={handleClick} >
                <img src={`/images/editor/icons/elements/${realElement.type}.svg`} alt={realElement.type} height="30px" />
            </Media>
            <Media body className="text-truncate"  onClick={handleClick} >
            
                {canFixClauseOrder && ['clause', 'statement'].includes(realElement.type) && 
                    <> 
                        {/* Block:<input type="text" id="block" value="{{block}}" style={{width:20, color: "black"}}/>
                        #:<input type="text" id="number" value="{{number}}" style={{width:20, color: "black"}}/> */}
                    </> 
                }
                {realElement.type === 'signature' && t('editor.index.signature')}
                {realElement.type === 'break' && t('editor.index.breakline')}
                {realElement.type === 'clause' && `${getOrdinalNumber({  number: realElement.number || realElement.index, t })}. `}
                {realElement.type === 'statement' && `${realElement.number || realElement.index}. `}
                {content}
            </Media>
            <Media>
            {isEditable && 
                <Button color="light" size="sm" onClick={handleRemove}>
                        <i className="fas fa-times text-danger"></i>
                </Button>
            }
            </Media>
            </Media>
        </div>
    )
})